import React, { useState, useEffect, useCallback } from "react";
import { Container, Row, Col, Card, Button, Spinner, Badge, InputGroup, Form } from "react-bootstrap";
import { Search, MailOpen, Trash2, Send, Clock } from "lucide-react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import Swal from "sweetalert2";

import Header from "./Header";
import Footer from "./Footer";
import SideMenu from "./SideMenu";
import MemberMenu from "./MemberMenu";
import AdminMenu from "./AdminMenu";
import Logout from "./Logout";
import { useAuth } from "./Home1";

const DRAFT_API = "https://beaconsms.com/oldsite/api/drafts_api.php";

const DraftSms = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const isAdmin = user?.id === "71";
  const userId = user?.id;

  const [drafts, setDrafts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");

  const fetchDrafts = useCallback(async () => {
    if (!userId) return;
    setLoading(true);
    try {
      const response = await axios.get(`${DRAFT_API}?user_id=${userId}`);
      setDrafts(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error("Error fetching drafts:", error);
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    document.title = "Draft SMS - beaconSMS";
    fetchDrafts();
  }, [fetchDrafts]);

  const handleDelete = (draftId) => {
    Swal.fire({
      title: "Delete Draft?",
      text: "You won't be able to revert this!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      confirmButtonText: "Yes, delete it!"
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          await axios.post(DRAFT_API, { action: "delete", draftId, userId });
          setDrafts(drafts.filter(d => d.id !== draftId));
          Swal.fire("Deleted!", "Draft removed.", "success");
        } catch (error) {
          Swal.fire("Error", "Failed to delete draft.", "error");
        }
      }
    });
  };

  const handleEditAndSend = (draft) => {
    // Navigate back to your Bulk SMS page and pass the draft data via state
    navigate("/compose", { 
      state: { 
        sender: draft.sender_id, 
        recipients: draft.recipients, 
        message: draft.message 
      } 
    });
  };

  const filteredDrafts = drafts.filter(d => 
    d.message.toLowerCase().includes(searchTerm.toLowerCase()) || 
    d.sender_id.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <Container>
      <Header user={user} />
      <Row className='mt-3 px-md-3'>
        <Col md='3' className='mb-3 mb-md-0'>
          <Logout Menu='Login Menu' user={user?.username} />
          <SideMenu Menu="Member's Menu" menuu={MemberMenu} />
          {isAdmin && <SideMenu Menu="Admin's Menu" menuu={AdminMenu} />}
        </Col>

        <Col md='9'>
          <div className="bg-white p-4 border rounded shadow-sm min-vh-50">
            <div className="d-flex justify-content-between align-items-center mb-4 border-bottom pb-3">
              <div className="d-flex align-items-center">
                <MailOpen className="text-primary me-2" size={24} />
                <h4 className="mb-0 fw-bold text-primary">Draft Messages</h4>
              </div>
              <Badge bg="primary-subtle" className="text-primary px-3 py-2 border border-primary-subtle">
                {drafts.length} Drafts Saved
              </Badge>
            </div>

            <InputGroup className="mb-4 shadow-sm">
              <InputGroup.Text className="bg-light border-end-0">
                <Search size={18} className="text-muted" />
              </InputGroup.Text>
              <Form.Control
                className="border-start-0"
                placeholder="Search by sender or message content..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </InputGroup>

            {loading ? (
              <div className="text-center py-5"><Spinner animation="border" variant="primary" /></div>
            ) : filteredDrafts.length > 0 ? (
              <Row xs={1} className="g-3">
                {filteredDrafts.map((draft) => (
                  <Col key={draft.id}>
                    <Card className="shadow-sm border-0 bg-light card-hover">
                      <Card.Body className="p-3">
                        <div className="d-flex justify-content-between">
                          <div className="w-75">
                            <div className="d-flex align-items-center mb-1">
                              <span className="fw-bold text-dark me-2">{draft.sender_id}</span>
                              <small className="text-muted d-flex align-items-center">
                                <Clock size={12} className="me-1" /> {draft.created_at}
                              </small>
                            </div>
                            <p className="text-muted small mb-0 text-truncate-2" style={{lineHeight: '1.4'}}>
                              {draft.message}
                            </p>
                            <small className="text-primary mt-1 d-block fw-bold" style={{fontSize: '11px'}}>
                              To: {draft.recipients.substring(0, 30)}...
                            </small>
                          </div>
                          <div className="d-flex flex-column gap-2 justify-content-center">
                            <Button variant="primary" size="sm" onClick={() => handleEditAndSend(draft)}>
                              <Send size={14} className="me-1" /> Send
                            </Button>
                            <Button variant="outline-danger" size="sm" onClick={() => handleDelete(draft.id)}>
                              <Trash2 size={14} />
                            </Button>
                          </div>
                        </div>
                      </Card.Body>
                    </Card>
                  </Col>
                ))}
              </Row>
            ) : (
              <div className="text-center py-5 text-muted border rounded bg-light border-dashed">
                <MailOpen size={48} className="mb-2 opacity-25" />
                <p>No drafts found. Messages you save while composing will appear here.</p>
              </div>
            )}
          </div>
        </Col>
      </Row>
      <Footer />
    </Container>
  );
};

export default DraftSms;